import { useState } from "react";
import { Link } from "react-router-dom";
import { Search, HelpCircle, Home, Users, CreditCard, Shield, MessageCircle, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

const FAQPage = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [activeCategory, setActiveCategory] = useState("all");

  const categories = [
    { id: "all", label: "All Questions", icon: HelpCircle },
    { id: "homeowners", label: "For Homeowners", icon: Home },
    { id: "contractors", label: "For Contractors", icon: Users },
    { id: "payments", label: "Payments & Pricing", icon: CreditCard },
    { id: "trust", label: "Trust & Safety", icon: Shield },
  ];

  const faqs = [
    // Homeowner FAQs
    {
      id: "h1",
      category: "homeowners",
      question: "How do I request a service?",
      answer: "Simply click 'Request a Service' on our homepage, describe your project, select your preferred service category, and provide your contact details. We'll match you with qualified local professionals within minutes."
    },
    {
      id: "h2",
      category: "homeowners",
      question: "Is there a fee for homeowners to use LocalPros?",
      answer: "No! LocalPros is completely free for homeowners. You can post jobs, receive quotes, and connect with professionals without paying any fees. Our service is funded by contractors who pay for quality leads."
    },
    {
      id: "h3",
      category: "homeowners",
      question: "How are contractors vetted?",
      answer: "All contractors on LocalPros go through a verification process. We verify their business information, check licenses where applicable, and review customer feedback. Contractors can earn a 'Verified' badge by completing additional background checks and providing proof of insurance."
    },
    {
      id: "h4",
      category: "homeowners",
      question: "Can I see reviews before hiring a contractor?",
      answer: "Yes! Every contractor profile displays their customer reviews, overall rating, photos of past work, and verification status. We encourage you to review these details before making your decision."
    },
    {
      id: "h5",
      category: "homeowners",
      question: "What if I'm not satisfied with the work?",
      answer: "Customer satisfaction is our priority. If you have issues with a contractor's work, first try to resolve it directly with them. You can also leave an honest review and contact our support team. We take quality complaints seriously and may remove contractors who consistently underperform."
    },
    {
      id: "h6",
      category: "homeowners",
      question: "How quickly will I get matched with a contractor?",
      answer: "Most homeowners receive their first contractor match within minutes of submitting a request. The speed depends on your location and the type of service needed, but our AI-powered matching system works around the clock to connect you with available pros."
    },

    // Contractor FAQs
    {
      id: "c1",
      category: "contractors",
      question: "How do I join LocalPros as a contractor?",
      answer: "Click 'Join as a Pro' and complete our simple registration form. You'll need to provide your business information, services offered, service areas, and contact details. Once approved, you can start viewing and purchasing leads immediately."
    },
    {
      id: "c2",
      category: "contractors",
      question: "What types of leads can I access?",
      answer: "You'll have access to leads in your selected service categories and geographic areas. Each lead includes the customer's project description, timeline, budget range, and contact information once unlocked."
    },
    {
      id: "c3",
      category: "contractors",
      question: "How does the lead system work?",
      answer: "When a homeowner submits a service request in your area, you'll be notified. You can view the job details (location, service type, description) for free. To get the customer's contact information and reach out, you'll need to unlock the lead through pay-per-lead or a subscription."
    },
    {
      id: "c4",
      category: "contractors",
      question: "How do I get the 'Verified' badge?",
      answer: "To earn the Verified badge, you need to complete additional verification steps: provide proof of valid business license, pass a background check, and show proof of liability insurance. Verified contractors get higher visibility and more customer trust."
    },
    {
      id: "c5",
      category: "contractors",
      question: "Can I choose which leads to purchase?",
      answer: "Absolutely! With pay-per-lead, you only pay for the leads you're interested in. You can review job details before deciding to unlock. Subscription members can unlock leads included in their monthly allowance or purchase additional ones."
    },
    {
      id: "c6",
      category: "contractors",
      question: "How do customer reviews work?",
      answer: "After completing a job, customers can leave a review on your profile. Reviews include a 1-5 star rating and written feedback. Maintaining high ratings improves your visibility on the platform and increases your chances of being matched with new customers."
    },

    // Payment FAQs
    {
      id: "p1",
      category: "payments",
      question: "What are the pricing options for contractors?",
      answer: "We offer flexible pricing: Pay-per-lead at $15 per lead, Basic subscription at $29/month (includes 5 leads), or Pro subscription at $49/month (unlimited leads + featured listing). Choose what works best for your business volume."
    },
    {
      id: "p2",
      category: "payments",
      question: "What payment methods do you accept?",
      answer: "We accept all major credit cards (Visa, Mastercard, American Express) and cryptocurrency through our secure Stripe payment integration. All transactions are encrypted and secure."
    },
    {
      id: "p3",
      category: "payments",
      question: "Can I cancel my subscription anytime?",
      answer: "Yes, you can cancel your subscription at any time. Your access will continue until the end of your current billing period. There are no cancellation fees or long-term commitments."
    },
    {
      id: "p4",
      category: "payments",
      question: "Is there a refund policy for leads?",
      answer: "We offer refunds for leads that contain invalid contact information or if the customer confirms they never submitted a request. Contact our support team within 48 hours of purchasing a lead to request a review."
    },
    {
      id: "p5",
      category: "payments",
      question: "Do subscription leads roll over?",
      answer: "No, unused leads from the Basic plan do not roll over to the next month. We recommend upgrading to Pro for unlimited leads if you consistently need more than 5 leads per month."
    },

    // Trust & Safety FAQs
    {
      id: "t1",
      category: "trust",
      question: "How do you protect my personal information?",
      answer: "We use industry-standard encryption and security practices to protect your data. Your personal information is only shared with contractors you choose to connect with. We never sell your data to third parties."
    },
    {
      id: "t2",
      category: "trust",
      question: "Are contractors insured?",
      answer: "We encourage all contractors to maintain proper insurance. Contractors with verified insurance display an 'Insured' badge on their profile. We recommend always confirming insurance coverage before starting any project."
    },
    {
      id: "t3",
      category: "trust",
      question: "What if a contractor doesn't respond?",
      answer: "If a contractor doesn't respond within 24 hours, your request is automatically shared with additional qualified pros. You can also browse our directory and contact contractors directly through their profiles."
    },
    {
      id: "t4",
      category: "trust",
      question: "How do you handle disputes?",
      answer: "For disputes, we recommend first attempting to resolve issues directly with the contractor. If that fails, our support team can mediate. We review all complaints and may remove contractors who violate our terms or consistently receive negative feedback."
    },
    {
      id: "t5",
      category: "trust",
      question: "Can I report a fraudulent contractor?",
      answer: "Yes, please report any suspicious activity immediately through our contact form or by emailing support. We take fraud seriously and investigate all reports. Confirmed fraudulent accounts are permanently banned."
    },
  ];

  const filteredFaqs = faqs.filter((faq) => {
    const matchesCategory = activeCategory === "all" || faq.category === activeCategory;
    const matchesSearch = searchQuery === "" || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-[#f8f9fa] to-white" data-testid="faq-hero">
        <div className="container-custom text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#e8f0fe] rounded-full mb-6">
            <HelpCircle className="w-4 h-4 text-[#1a73e8]" />
            <span className="text-sm font-medium text-[#1a73e8]">Help Center</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-medium text-[#202124] mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-[#5f6368] max-w-2xl mx-auto mb-8">
            Find answers to common questions about LocalPros for both homeowners and contractors
          </p>
          
          {/* Search Bar */}
          <div className="max-w-xl mx-auto relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#80868b] w-5 h-5" />
            <input
              type="text"
              placeholder="Search for answers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full h-14 pl-12 pr-4 rounded-full border border-[#dadce0] focus:outline-none focus:border-[#1a73e8] focus:ring-2 focus:ring-[#1a73e8]/20 transition-all text-[#202124]"
              data-testid="faq-search-input"
            />
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="py-8 border-b border-[#dadce0]" data-testid="faq-categories">
        <div className="container-custom">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map((cat) => (
              <button
                key={cat.id}
                onClick={() => setActiveCategory(cat.id)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium transition-all ${
                  activeCategory === cat.id
                    ? "bg-[#1a73e8] text-white shadow-md"
                    : "bg-[#f8f9fa] text-[#5f6368] hover:bg-[#e8f0fe] hover:text-[#1a73e8]"
                }`}
                data-testid={`faq-category-${cat.id}`}
              >
                <cat.icon className="w-4 h-4" />
                {cat.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Accordion */}
      <section className="py-16" data-testid="faq-list">
        <div className="container-custom max-w-3xl">
          {filteredFaqs.length === 0 ? (
            <div className="text-center py-12">
              <HelpCircle className="w-16 h-16 text-[#dadce0] mx-auto mb-4" />
              <h3 className="text-xl font-medium text-[#202124] mb-2">No results found</h3>
              <p className="text-[#5f6368]">
                Try adjusting your search or browse all categories
              </p>
              <Button
                variant="outline"
                onClick={() => { setSearchQuery(""); setActiveCategory("all"); }}
                className="mt-4 rounded-full"
              >
                Clear filters
              </Button>
            </div>
          ) : (
            <Accordion type="single" collapsible className="space-y-4">
              {filteredFaqs.map((faq) => (
                <AccordionItem
                  key={faq.id}
                  value={faq.id}
                  className="bg-[#f8f9fa] rounded-2xl border-none px-6 data-[state=open]:bg-white data-[state=open]:shadow-md data-[state=open]:border data-[state=open]:border-[#dadce0]"
                  data-testid={`faq-item-${faq.id}`}
                >
                  <AccordionTrigger className="hover:no-underline py-5 text-left">
                    <div className="flex items-start gap-3">
                      <span className={`mt-0.5 px-2 py-0.5 rounded text-xs font-medium ${
                        faq.category === "homeowners" ? "bg-[#e8f0fe] text-[#1a73e8]" :
                        faq.category === "contractors" ? "bg-[#e6f4ea] text-[#1e8e3e]" :
                        faq.category === "payments" ? "bg-[#fef7e0] text-[#ea8600]" :
                        "bg-[#fce8e6] text-[#d93025]"
                      }`}>
                        {faq.category === "homeowners" ? "Homeowner" :
                         faq.category === "contractors" ? "Contractor" :
                         faq.category === "payments" ? "Payment" : "Safety"}
                      </span>
                      <span className="font-medium text-[#202124]">{faq.question}</span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 pl-[60px] text-[#5f6368] leading-relaxed">
                    {faq.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          )}
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-[#f8f9fa]" data-testid="faq-contact-cta">
        <div className="container-custom text-center">
          <MessageCircle className="w-12 h-12 text-[#1a73e8] mx-auto mb-4" />
          <h2 className="text-2xl md:text-3xl font-medium text-[#202124] mb-4">
            Still have questions?
          </h2>
          <p className="text-[#5f6368] max-w-xl mx-auto mb-8">
            Can't find what you're looking for? Our support team is here to help you.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button
              className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-8 py-3 h-auto"
              data-testid="contact-support-btn"
            >
              Contact Support <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <Link to="/request">
              <Button
                variant="outline"
                className="rounded-full border-[#dadce0] px-8 py-3 h-auto w-full sm:w-auto"
                data-testid="request-service-faq-btn"
              >
                Request a Service
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default FAQPage;
